import clsx from "clsx";
import Icon from "@/components/icon";
import { trackVideoProgress } from "@/features/analytics";
import { useVideoAnalytics } from "./hooks/use-video-analytics";
import { useHlsController } from "./hooks/use-hls-controller";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { formatSecondsToHMS, formatTime } from "./utils/player-utils";
import {
  FastForwardIndicator,
  SettingsMenu,
  VideoProgressBar,
} from "./components";
import { CoreVideo } from "./components/core-video";
import { useControlsVisibility } from "./hooks/use-controls-visibility";
import { useVideoFullscreen } from "./hooks/use-video-fullscreen";
import { useVideoGestures } from "./hooks/use-video-gestures";
import { useVideoPlayback } from "./hooks/use-video-playback";
import { useVideoState } from "./hooks/use-video-state";

interface AmoozimPlayerProps {
  id?: string;
  src?: string;
  payload?: string;
  className?: string;
  autoplay?: boolean;
  muted?: boolean;
  controls?: boolean;
  disableAuthHeaders?: boolean;
  onError?: (error: Error) => void;
  onReady?: () => void;
  analytics?: {
    miniappId: string;
    courseId?: number;
    lessonId?: number;
  };
}

const AmoozimPlayer: React.FC<AmoozimPlayerProps> = ({
  id,
  src,
  payload,
  className = "",
  muted = false,
  onError,
  onReady,
  disableAuthHeaders,
  analytics: analyticsMeta,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // State Hooks
  const {
    currentTime,
    setCurrentTime,
    duration,
    setDuration,
    buffering,
    setBuffering,
    buffered,
    setBuffered,
    hasStarted,
    setHasStarted,
    isDraggingProgress,
    setIsDraggingProgress,
  } = useVideoState();

  // Control Visibility Hook
  const {
    showControls,
    showControlsWithTimeout,
    hideControlsAfterDelay,
    clearControlsTimeout,
  } = useControlsVisibility();

  const handlePlayChange = useCallback(
    (playing: boolean) => {
      if (playing) {
        hideControlsAfterDelay();
      } else {
        clearControlsTimeout();
        showControlsWithTimeout(); // Show controls when paused
      }
    },
    [hideControlsAfterDelay, clearControlsTimeout, showControlsWithTimeout],
  );

  // Playback Hook
  const {
    isPlaying,
    playbackSpeed,
    showSpeedIndicator,
    togglePlay,
    handleVideoPlay: onVideoPlayStateChange,
    handleVideoPause: onVideoPauseStateChange,
    handleSpeedChange,
    enableDoubleSpeed,
    disableDoubleSpeed,
  } = useVideoPlayback({
    videoRef,
    onPlayChange: handlePlayChange,
  });

  // Fullscreen Hook
  const { isFullscreen, toggleFullscreen } = useVideoFullscreen({
    onToggle: showControlsWithTimeout,
  });

  // Analytics
  const analyticsConfig = useMemo(
    () => ({
      miniappId: analyticsMeta?.miniappId,
      lessonId: analyticsMeta?.lessonId,
      contentId: id ? Number(id) : undefined,
    }),
    [analyticsMeta?.miniappId, analyticsMeta?.lessonId, id],
  );

  const analytics = useVideoAnalytics(analyticsConfig);

  // Marketing `video_progress`: bucketed at 25/50/75/100% only — never a
  // per-second stream. Each bucket is emitted at most once per content item.
  const marketingBucketsRef = useRef<Set<number>>(new Set());
  useEffect(() => {
    marketingBucketsRef.current = new Set();
  }, [id]);

  const reportVideoProgress = useCallback(
    (timeSeconds: number, totalSeconds: number) => {
      const meta = analyticsMeta;
      if (
        !meta?.miniappId ||
        typeof meta.courseId !== "number" ||
        typeof meta.lessonId !== "number"
      )
        return;
      if (
        !Number.isFinite(timeSeconds) ||
        !Number.isFinite(totalSeconds) ||
        totalSeconds <= 0
      )
        return;

      const pct = (timeSeconds / totalSeconds) * 100;
      ([25, 50, 75, 100] as const).forEach((bucket) => {
        if (pct >= bucket && !marketingBucketsRef.current.has(bucket)) {
          marketingBucketsRef.current.add(bucket);
          trackVideoProgress({
            course_id: meta.courseId!,
            lesson_id: meta.lessonId!,
            mini_app_uuid: meta.miniappId,
            video_id: id ? String(id) : undefined,
            progress_percent: bucket,
            watch_time_seconds: Math.round(timeSeconds),
            video_duration_seconds: Math.round(totalSeconds),
          });
        }
      });
    },
    [analyticsMeta, id],
  );

  const lastPlayStartRef = useRef(0);
  const dragStartTimeRef = useRef<number | null>(null);
  const hasSentStartRef = useRef(false);
  const hasFlushedOnPageHideRef = useRef(false);
  const currentTimeRef = useRef(0);

  const trackProgressUntil = useCallback(
    (segmentEndSeconds: number) => {
      if (!analytics || !hasStarted) return;
      const segmentStartSeconds = lastPlayStartRef.current ?? 0;
      const startSeconds = Number(segmentStartSeconds.toFixed(3));
      const endSeconds = Number(segmentEndSeconds.toFixed(3));
      if (startSeconds === endSeconds) return;
      analytics.trackProgress({
        offset: formatSecondsToHMS(startSeconds),
        end_seconds: formatSecondsToHMS(endSeconds),
      });
    },
    [analytics, hasStarted],
  );

  useEffect(() => {
    currentTimeRef.current = currentTime;
  }, [currentTime]);

  const flushProgress = useCallback(() => {
    if (!analytics || !hasStarted) return;

    const endSeconds = videoRef.current?.currentTime ?? currentTimeRef.current;
    if (Number.isNaN(endSeconds)) return;

    trackProgressUntil(endSeconds);
    lastPlayStartRef.current = endSeconds;
  }, [analytics, hasStarted, trackProgressUntil]);

  // HLS Controller
  const { loading, qualities, currentQuality, handleQualityChange } =
    useHlsController({
      videoRef,
      src,
      payload,
      disableAuthHeaders,
      onReady,
      onError,
      onBufferingChange: setBuffering,
    });

  const handleGestureSeek = useCallback(
    (from: number, to: number) => {
      if (!analytics || !hasStarted || dragStartTimeRef.current !== null)
        return;
      const fromSeconds = Number(from);
      const toSeconds = Number(to);
      if (fromSeconds === toSeconds) return;
      trackProgressUntil(fromSeconds);
      analytics.trackJump({
        from: formatSecondsToHMS(fromSeconds),
        to: formatSecondsToHMS(toSeconds),
      });
      lastPlayStartRef.current = toSeconds;
    },
    [analytics, hasStarted, trackProgressUntil],
  );

  // Gestures Hook
  const {
    seekAnimation,
    handleClick,
    handleTouchStart,
    handleTouchEnd,
    handleMouseDown,
    handleMouseUp,
  } = useVideoGestures({
    videoRef,
    containerRef,
    onTap: showControlsWithTimeout,
    onDoubleTap: showControlsWithTimeout,
    onLongPressStart: enableDoubleSpeed,
    onLongPressEnd: disableDoubleSpeed,
    onSeek: handleGestureSeek,
  });

  // Effects
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const updateBuffered = () => {
      setBuffered(video.buffered);
    };

    video.addEventListener("progress", updateBuffered);
    video.addEventListener("timeupdate", updateBuffered); // Fallback

    return () => {
      video.removeEventListener("progress", updateBuffered);
      video.removeEventListener("timeupdate", updateBuffered);
    };
  }, [setBuffered]);

  // Handlers
  const handleSeek = (newTime: number, isPreview = false) => {
    const video = videoRef.current;
    if (!video) return;

    setCurrentTime(newTime);
    if (Math.abs(newTime) < 0.001) {
      hasSentStartRef.current = false;
      lastPlayStartRef.current = 0;
    }

    if (!isPreview) {
      video.currentTime = newTime;
    } else {
      const wasPlaying = !video.paused;
      if (wasPlaying) {
        video.pause();
      }

      video.currentTime = newTime;

      const videoWithCallback = video as HTMLVideoElement & {
        requestVideoFrameCallback?: (callback: () => void) => void;
      };

      if (videoWithCallback.requestVideoFrameCallback) {
        videoWithCallback.requestVideoFrameCallback(() => {
          if (wasPlaying) {
            video.play().catch(() => {});
          }
        });
      } else {
        setTimeout(() => {
          if (wasPlaying) {
            video.play().catch(() => {});
          }
        }, 0);
      }
    }

    showControlsWithTimeout();
  };

  const handleVideoPlay = useCallback(
    (event: React.SyntheticEvent<HTMLVideoElement>) => {
      onVideoPlayStateChange();
      const video = event.currentTarget;
      if (analytics) {
        const segmentStartSeconds = video.currentTime;
        lastPlayStartRef.current = segmentStartSeconds;
        const atBeginning = segmentStartSeconds <= 0.5;
        if (atBeginning && !hasSentStartRef.current) {
          const startOffset = formatSecondsToHMS(segmentStartSeconds);
          analytics.trackClick();
          analytics.trackStart({
            offset: startOffset,
            end_seconds: startOffset,
          });
          hasSentStartRef.current = true;
        }
        setHasStarted(true);
      }
    },
    [analytics, onVideoPlayStateChange, setHasStarted],
  );

  const handleVideoPause = useCallback(
    (event: React.SyntheticEvent<HTMLVideoElement>) => {
      onVideoPauseStateChange();
      if (event.currentTarget.ended) {
        return;
      }
      if (analytics && hasStarted) {
        const segmentStartSeconds =
          lastPlayStartRef.current ?? event.currentTarget.currentTime;
        if (segmentStartSeconds === undefined) {
          return;
        }
        const startOffset = formatSecondsToHMS(segmentStartSeconds);
        const endOffset = formatSecondsToHMS(event.currentTarget.currentTime);
        if (startOffset === endOffset) {
          return;
        }
        analytics.trackProgress({
          offset: startOffset,
          end_seconds: endOffset,
        });
      }
    },
    [analytics, hasStarted, onVideoPauseStateChange],
  );

  const handleTimeUpdate = useCallback(() => {
    if (videoRef.current && !isDraggingProgress) {
      setCurrentTime(videoRef.current.currentTime);
      reportVideoProgress(
        videoRef.current.currentTime,
        videoRef.current.duration,
      );
    }
  }, [isDraggingProgress, setCurrentTime, reportVideoProgress]);

  const handleLoadedMetadata = useCallback(() => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  }, [setDuration]);

  const handleEnded = useCallback(() => {
    // Ensure the 100% bucket lands even if `timeupdate` never reports the tail.
    reportVideoProgress(
      duration || videoRef.current?.duration || 0,
      duration || videoRef.current?.duration || 0,
    );
    if (analytics && hasStarted) {
      const segmentStartSeconds = lastPlayStartRef.current ?? 0;
      const startOffset = formatSecondsToHMS(segmentStartSeconds);
      const endSeconds = formatSecondsToHMS(
        duration || videoRef.current?.duration || 0,
      );
      analytics.trackEnd({
        offset: startOffset,
        end_seconds: endSeconds,
      });
      lastPlayStartRef.current = 0;
      setHasStarted(false);
      hasSentStartRef.current = false;
    }
  }, [analytics, duration, hasStarted, setHasStarted, reportVideoProgress]);

  useEffect(() => {
    const handlePopState = () => {
      if (hasFlushedOnPageHideRef.current) return;
      hasFlushedOnPageHideRef.current = true;
      flushProgress();
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      flushProgress();
      window.removeEventListener("popstate", handlePopState);
      hasFlushedOnPageHideRef.current = false;
    };
  }, [flushProgress]);

  const handleWaiting = useCallback(() => setBuffering(true), [setBuffering]);
  const handleCanPlay = useCallback(() => setBuffering(false), [setBuffering]);

  // Derived Control Handlers
  const handleDragStart = useCallback(() => {
    setIsDraggingProgress(true);
    dragStartTimeRef.current = videoRef.current?.currentTime ?? null;
  }, [setIsDraggingProgress]);

  const handleDragEnd = useCallback(() => {
    setIsDraggingProgress(false);
    const dragStartSeconds = dragStartTimeRef.current;
    dragStartTimeRef.current = null;
    showControlsWithTimeout();
    if (!analytics) return;
    if (dragStartSeconds === null) return;
    const finalTime = videoRef.current?.currentTime;
    if (finalTime === undefined || Number.isNaN(finalTime)) return;
    const fromSeconds = Number(dragStartSeconds.toFixed(3));
    const toSeconds = Number(finalTime.toFixed(3));
    if (fromSeconds === toSeconds) return;
    trackProgressUntil(fromSeconds);
    analytics.trackJump({
      from: formatSecondsToHMS(fromSeconds),
      to: formatSecondsToHMS(toSeconds),
    });
    lastPlayStartRef.current = toSeconds;
    if (toSeconds <= 0.5) {
      hasSentStartRef.current = false;
    }
  }, [
    analytics,
    setIsDraggingProgress,
    showControlsWithTimeout,
    trackProgressUntil,
  ]);

  return (
    <div
      ref={containerRef}
      className={clsx(
        "overflow-hidden bg-black group w-full",
        isFullscreen
          ? "fixed inset-0 z-50 rounded-none bg-black"
          : "rounded-xl relative",
        className,
      )}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
    >
      <div className="relative w-full h-full">
        <CoreVideo
          ref={videoRef}
          muted={muted}
          onWaiting={handleWaiting}
          onCanPlay={handleCanPlay}
          onPlay={handleVideoPlay}
          onPause={handleVideoPause}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={handleEnded}
        />

        {seekAnimation && <FastForwardIndicator side={seekAnimation} />}

        {showSpeedIndicator && (
          <div className="absolute top-10 left-0 right-0 flex justify-center z-50">
            <div className="bg-[#000000cc] text-white text-sm font-medium px-4 py-2 rounded-full flex items-center gap-2">
              <Icon name="fast-forward" size={16} />
              <span>2×</span>
            </div>
          </div>
        )}
      </div>

      {loading && (
        <div className="absolute inset-0 bg-[#00000080] flex items-center justify-center">
          <div className="size-8 border-2 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {showControls && !isDraggingProgress && (
        <div className="absolute inset-0 pointer-events-none">
          {buffering && (
            <div className="absolute inset-0 bg-[#00000080] flex items-center justify-center">
              <div className="size-8 border-2 border-t-transparent border-white rounded-full animate-spin" />
            </div>
          )}

          {!buffering && !isPlaying && (
            <div className="absolute inset-0 flex items-center justify-center">
              <button
                type="button"
                className="rounded-full bg-[#00000080] size-14 flex items-center justify-center outline-none pointer-events-auto backdrop-blur-sm hover:bg-[#000000b3] transition-all"
                onClick={togglePlay}
              >
                <Icon name="play-fill" size={36} className="text-white" />
              </button>
            </div>
          )}

          {!buffering && isPlaying && (
            <div className="absolute inset-0 flex items-center justify-center">
              <button
                type="button"
                className="rounded-full bg-[#00000080] size-14 flex items-center justify-center outline-none pointer-events-auto backdrop-blur-sm hover:bg-[#000000b3] transition-all animate-fade-out"
                onClick={togglePlay}
              >
                <Icon name="pause-fill" size={36} className="text-white" />
              </button>
            </div>
          )}
        </div>
      )}

      <div
        className={`${
          showControls ? "opacity-100" : "opacity-0"
        } pointer-events-none absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-black/80 via-black/40 to-transparent transition-opacity duration-300`}
      />

      <div
        className={`${
          showControls ? "opacity-100" : "opacity-0"
        } absolute bottom-0 left-0 right-0 px-4 py-1 transition-opacity duration-300`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={toggleFullscreen}
              className="text-white hover:text-gray-300"
              aria-label={isFullscreen ? "Minimize" : "Fullscreen"}
            >
              {isFullscreen ? (
                <Icon name="minimize" className="w-5 h-5" />
              ) : (
                <Icon name="fullscreen" className="w-5 h-5" />
              )}
            </button>
            <SettingsMenu
              currentQuality={currentQuality}
              currentSpeed={playbackSpeed}
              onQualityChange={handleQualityChange}
              onSpeedChange={handleSpeedChange}
              qualities={qualities}
            />
          </div>
          <div className="flex items-center gap-4">
            <span className="text-white text-sm">
              {formatTime(duration)} / {formatTime(currentTime)}
            </span>
          </div>
        </div>

        {duration > 0 && (
          <VideoProgressBar
            currentTime={currentTime}
            duration={duration}
            buffered={buffered}
            onSeek={handleSeek}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          />
        )}
      </div>
    </div>
  );
};

export default AmoozimPlayer;
